package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.milta.core.localization.AiCreditStrings
import com.milta.core.localization.OptimizationQuotaStrings
import com.milta.feature.aicredit.domain.entity.AiCreditOutcome
import com.milta.feature.aicredit.presentation.AiCreditHistoryState
import com.milta.feature.aicredit.presentation.outcomeLabel
import com.milta.feature.optimizationquota.presentation.fillPlaceholders
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.w3c.dom.HTMLElement

/** MIL-98 — building blocks of the AI credit on the web dashboard (Enterprise + Admin). */

/** Distance to the bottom (px) at which the next history page is requested (R13). */
private const val AI_LOAD_MORE_THRESHOLD_PX = 40

/** Lets the new rows be laid out before measuring whether the box overflows. */
private const val AI_LAYOUT_SETTLE_MS = 50L

internal const val WebWarnInk = "#8A5A0A"
internal const val WebLowInk = "#B26A00"
internal const val WebLowBg = "#FFF1DC"

private class AiElementHolder {
    var element: HTMLElement? = null
}

/** Meter of the cards: [percent] of the bar filled, orange when [low]. */
@Composable
internal fun CreditMeter(percent: Int, low: Boolean) {
    Div(attrs = { style { property("height", "8px"); property("border-radius", "999px"); property("background", WebSurfaceDim); property("margin", "10px 0 6px"); property("overflow", "hidden") } }) {
        Div(attrs = {
            style {
                property("height", "100%"); property("width", "${percent.coerceIn(0, 100)}%")
                property("background", if (low) WebLowInk else WebTealAccent); property("border-radius", "999px")
            }
        }) {}
    }
}

/** "Réussi" / "Échec" / "Illisible" pill of a history row (R3). */
@Composable
internal fun AiOutcomePill(outcome: AiCreditOutcome, strings: AiCreditStrings) {
    val (bg, fg) = when (outcome) {
        AiCreditOutcome.Success -> WebSuccessBg to WebSuccessInk
        AiCreditOutcome.Failed -> WebErrorBg to WebErrorInk
        AiCreditOutcome.Unreadable -> WebLowBg to WebLowInk
    }
    Span(attrs = {
        style {
            padding(3.px, 8.px); property("border-radius", "999px"); property("white-space", "nowrap")
            property("font-size", "11px"); property("font-weight", "700"); property("background", bg); property("color", fg)
        }
    }) { Text(strings.outcomeLabel(outcome)) }
}

/** One column of [AiCreditHistoryTable]: header and right alignment. */
internal data class AiHistoryColumn(val label: String, val alignRight: Boolean = false)

/**
 * Paged AI credit history (E2 user rows, A2 admin rows): rows appended page by page when the box
 * reaches its bottom (R13) — never the whole list. [row] renders the cells of one item.
 */
@Composable
internal fun <T> AiCreditHistoryTable(
    history: AiCreditHistoryState<T>,
    columns: List<AiHistoryColumn>,
    footerStrings: OptimizationQuotaStrings,
    onLoadMore: () -> Unit,
    maxHeightPx: Int = 320,
    row: @Composable (T) -> Unit,
) {
    // Same safety net as the optimization history: a page that doesn't fill the box raises no
    // scroll event, the next one is asked for as long as the content doesn't overflow.
    val container = remember { AiElementHolder() }
    LaunchedEffect(history.loadedPages, history.isLoadingPage, history.error) {
        delay(AI_LAYOUT_SETTLE_MS)
        val el = container.element ?: return@LaunchedEffect
        if (history.loadedPages > 0 && history.hasMore && !history.isLoadingPage && history.error == null &&
            el.scrollHeight <= el.clientHeight
        ) {
            onLoadMore()
        }
    }
    Div(
        attrs = {
            ref { element ->
                container.element = element
                onDispose { container.element = null }
            }
            style {
                property("max-height", "${maxHeightPx}px"); property("overflow-y", "auto")
                property("border", "1px solid $WebBorder"); property("border-radius", "10px")
            }
            onScroll { event ->
                val el = event.target as? HTMLElement ?: return@onScroll
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - AI_LOAD_MORE_THRESHOLD_PX) onLoadMore()
            }
        },
    ) {
        Table(attrs = { style { property("width", "100%"); property("border-collapse", "collapse"); property("font-size", "12.5px") } }) {
            Thead {
                Tr {
                    columns.forEach { column ->
                        Th(attrs = {
                            style {
                                property("position", "sticky"); property("top", "0"); property("background", WebSurface)
                                property("text-align", if (column.alignRight) "right" else "left"); padding(8.px, 12.px)
                                property("font-size", "11px"); property("font-weight", "700"); property("color", WebTextSecondary)
                                property("text-transform", "uppercase"); property("border-bottom", "1px solid $WebBorder")
                            }
                        }) { Text(column.label) }
                    }
                }
            }
            Tbody {
                history.items.forEach { item -> Tr { row(item) } }
                Tr {
                    Td(attrs = {
                        attr("colspan", columns.size.toString())
                        style { padding(10.px, 12.px); property("text-align", "center"); property("font-size", "12px"); property("color", WebTextSecondary) }
                    }) {
                        when {
                            history.isLoadingPage -> Div(attrs = { style { property("display", "inline-flex"); property("gap", "8px"); property("align-items", "center") } }) {
                                WebSpinner(size = 14, strokeWidth = 2)
                                Text(footerStrings.historyLoadingMore)
                            }
                            history.error != null -> {
                                Span(attrs = { style { property("color", WebErrorInk); property("margin-right", "8px") } }) {
                                    Text(if (history.error == "offline") footerStrings.errorNoConnection else footerStrings.historyLoadError)
                                }
                                Button(attrs = { onClick { onLoadMore() }; style { secondaryButtonStyle(this) } }) { Text(footerStrings.retryAction) }
                            }
                            history.isEmpty -> Text(footerStrings.historyEmpty)
                            !history.hasMore -> Text(footerStrings.historyEnd.fillPlaceholders("count" to history.items.size))
                        }
                    }
                }
            }
        }
    }
}

/** One cell of [AiCreditHistoryTable]. */
@Composable
internal fun AiHistoryCell(text: String, alignRight: Boolean = false, bold: Boolean = false) {
    Td(attrs = {
        style {
            padding(8.px, 12.px); property("border-bottom", "1px solid $WebSurfaceDim"); property("color", WebTextPrimary)
            if (alignRight) property("text-align", "right")
            if (bold) property("font-weight", "700")
        }
    }) { Text(text) }
}

/** Cell holding a composable (outcome pill). */
@Composable
internal fun AiHistoryCellContent(content: @Composable () -> Unit) {
    Td(attrs = { style { padding(8.px, 12.px); property("border-bottom", "1px solid $WebSurfaceDim") } }) { content() }
}
